/**
 * Fetches distinct cities used in active courier routes (route_stops).
 * These cities may include courier-sourced entries (is_active = false)
 * that were auto-inserted from the cities_dataset.
 * Used to expand the Quote flow's city search beyond the curated list.
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface RouteCoveredCity {
  cityId: string;
  cityName: string;
  countryId: string;
  countryName: string;
}

async function fetchRouteCoveredCities(): Promise<Record<string, string[]>> {
  // Get all city IDs from route_stops on active routes
  const { data: stops, error: stopsErr } = await supabase
    .from("route_stops")
    .select(`
      city_id,
      country_id,
      route:courier_route_templates!route_stops_route_id_fkey(is_active)
    `)
    .limit(5000);

  if (stopsErr || !stops) return {};

  // Filter to active routes only
  const activeCityIds = new Set<string>();
  const cityToCountry = new Map<string, string>();

  for (const stop of stops) {
    const route = stop.route as any;
    if (route?.is_active) {
      activeCityIds.add(stop.city_id);
      cityToCountry.set(stop.city_id, stop.country_id);
    }
  }

  if (activeCityIds.size === 0) return {};

  // Fetch city names and country names
  const cityIds = [...activeCityIds];
  const countryIds = [...new Set(cityToCountry.values())];

  const [citiesRes, countriesRes] = await Promise.all([
    supabase.from("cities").select("id, name, country_id").in("id", cityIds),
    supabase.from("countries").select("id, name").in("id", countryIds),
  ]);

  const countryNameMap = new Map<string, string>();
  for (const c of countriesRes.data ?? []) {
    countryNameMap.set(c.id, c.name);
  }

  // Group by country name → city names
  const result: Record<string, string[]> = {};
  for (const city of citiesRes.data ?? []) {
    const countryName = countryNameMap.get(city.country_id);
    if (!countryName) continue;
    if (!result[countryName]) result[countryName] = [];
    if (!result[countryName].includes(city.name)) {
      result[countryName].push(city.name);
    }
  }

  // Sort city arrays
  for (const key of Object.keys(result)) {
    result[key].sort();
  }

  return result;
}

export const useRouteCoveredCities = () => {
  const { data, isLoading } = useQuery({
    queryKey: ["route-covered-cities"],
    queryFn: fetchRouteCoveredCities,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });

  return {
    routeCities: data ?? {},
    loading: isLoading,
  };
};
